import React from 'react';
import { ConfirmDialog, NumberInput, Textarea } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import { Field } from './sections';

/** The most one grant may give — the admin BFF's MAX_GRANT_CREDITS, which refuses above it. */
export const GRANT_MAX = 100000;
const NOTE_MAX = 500;

/** One id per grant attempt; the BFF turns it into the wallet's `admin-grant:<uuid>`. */
function newRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const b = globalThis.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Grant credits (پارسه) to the clinic's owner (PM-0211).
 *
 * Credits are a UNIT, never money, so the amount is whole credits with no
 * currency on any deployment. A grant is booked as a gift, not revenue, and
 * cannot be taken back from here — hence a second, verb-labelled confirmation
 * naming the amount and the recipient before anything is sent.
 *
 * ── A RETRY GRANTS ONCE ─────────────────────────────────────────────────────
 * The request id is made when the dialog opens and again whenever the amount
 * changes. A failed submit keeps the dialog open (TaskDialog) with the same id,
 * so pressing it again after a timeout that actually reached the wallet adds
 * nothing twice; a different amount is a different grant and gets a new id.
 */
export default function GrantCreditsDialog({ open, onOpenChange, clinic, pending, error, onSubmit }) {
  const { t, formatNumber } = useTranslation();

  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const [requestId, setRequestId] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setAmount('');
    setNote('');
    setConfirming(false);
  }, [open]);

  React.useEffect(() => { if (open) setRequestId(newRequestId()); }, [open, amount]);

  const value = Number(amount);
  const valid = /^\d+$/.test(amount) && value >= 1 && value <= GRANT_MAX;
  const invalid = amount !== '' && !valid;
  const max = formatNumber(GRANT_MAX);

  return (
    <>
      <TaskDialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('clinic.dialogs.grantCredits.title')}
        error={error}
        pending={pending}
        dirty={!!(amount || note)}
        submitLabel={t('clinic.dialogs.grantCredits.submit')}
        submitDisabled={!valid}
        onSubmit={() => setConfirming(true)}
        footerNote={t('clinic.dialogs.grantCredits.helper')}
        data-testid="detail-grant-credits-dialog"
      >
        <dl data-testid="detail-grant-recipient">
          <Field
            label={t('clinic.dialogs.grantCredits.recipient')}
            value={t('clinic.dialogs.grantCredits.recipientValue', { name: clinic.name })}
          />
        </dl>

        <div data-testid="detail-grant-amount">
          <NumberInput
            label={t('clinic.dialogs.grantCredits.amount')}
            required
            value={amount}
            onChange={(e) => setAmount((e.target?.value ?? e).replace(/\D/g, ''))}
            unit={t('common.units.parseh')}
            helperText={t('clinic.dialogs.grantCredits.amountHint', { max })}
            error={invalid ? t('clinic.dialogs.grantCredits.amountRange', { max }) : undefined}
          />
        </div>

        <Textarea
          label={t('clinic.dialogs.grantCredits.note')}
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          placeholder={t('clinic.dialogs.grantCredits.notePlaceholder')}
          maxLength={NOTE_MAX}
          showCount
          rows={2}
          data-testid="detail-grant-note"
        />
      </TaskDialog>

      {/* Stacked over the dialog that raised it — the one sanctioned stack. */}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('clinic.dialogs.grantCredits.confirmTitle', { amount: valid ? formatNumber(value) : '' })}
        description={t('clinic.dialogs.grantCredits.confirmBody', { name: clinic.name })}
        confirmLabel={t('clinic.dialogs.grantCredits.confirm')}
        cancelLabel={t('common.actions.cancel')}
        loading={pending}
        onConfirm={() => {
          setConfirming(false);
          onSubmit({ amount: value, note: note.trim() || null, requestId });
        }}
        data-testid="detail-grant-credits-confirm"
      />
    </>
  );
}
